import { serverTimestamp } from 'firebase/firestore';
import { getToken } from 'firebase/messaging';
import { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { _checkComponentTimes } from '../../../App';
import { Disclaimers } from '../../../Disclaimers';
import { allAppKeys, allowAppStorageAndFunctions } from '../../../project/appConfiguration';
import { appUserAccessPermissions } from '../../auth/appUserAccessPermissions';
import { allowMovement } from '../../components/map/mapHelper';
import { gEnums } from '../../enums/globalEnums';
import { updateAppUserFcmTokenToDatabase } from '../../firestoreData/profiles/updateToken';
import { currentHelpers } from '../../redirection/current';
import { _currentUserIdProp, appUserHandlers, appUserReducer, appUserReducerInitialState } from '../reducers/AppUserReducer';
import { AppContext } from './AppProvider';
import { AppUsersContext } from './AppUsersContext';
import { AuthContext } from './AuthContext';
import { DatabaseContext } from './DatabaseContext';
import { PapsContext } from './PapsContext';
import { ServiceWorkerContext } from './ServiceWorkerContext';
import { CurrentSettingsContext } from './SettingsDataContext';
import { StartContext } from './StartContext';
import { AppParentContext } from './AppParentContext';
import { useAppUserAccess } from './useHelpers/useAppUserAccess';
import { useFcm } from './useHelpers/useFcm';
import { _showAppLogs } from './LogContext';
import { useFavorites } from './useHelpers/useFavorites';
import { useProfile } from './useHelpers/useProfile';
import { useNavigate } from 'react-router-dom';

const _useDisclaimers = false

/** 
@provides appUser_state
@provides appUser_handlers 
*/
export const AppUserContext = createContext();

const AppUserProvider = (props) => {

  const navigate = useNavigate();

  // appParentContext
  const appParentContext = useContext(AppParentContext)
  const { fn_cls } = appParentContext ?? {}

  const appContext = useContext(AppContext)
  const { app_state } = appContext ?? {}
  const { mainAuth, mainDb, mainStorage, mainMessaging, currentEnvironment, isSmartPhone } = app_state ?? {}
  const { platform } = currentEnvironment ?? {}

  // parentContext not available  
  const startContext = useContext(StartContext)
  const { start_handlers, start_fns } = startContext ?? {}

  const serviceWorkerContext = useContext(ServiceWorkerContext)
  const { serviceWorker_state } = serviceWorkerContext ?? {}
  const { registration } = serviceWorker_state ?? {}

  // databaseContext
  const databaseContext = useContext(DatabaseContext)
  const { database_handlers, database_fns } = databaseContext ?? {}

  // authContext
  const authContext = useContext(AuthContext)
  const { auth_state } = authContext ?? {}
  const { currentAuthUser } = auth_state ?? {}
  const { presenceStatus } = currentAuthUser ?? {}

  // papsContext
  const appUsersContext = useContext(AppUsersContext)
  const { appUsers_state } = appUsersContext ?? {}
  const { eventAppUsers } = appUsers_state ?? {}

  // papsContext
  const papsContext = useContext(PapsContext)
  const { paps_state } = papsContext ?? {}
  const { hostName, pathViews, view, sharePathFull } = paps_state ?? {}

  // currentSettingsContext
  const currentSettingsContext = useContext(CurrentSettingsContext)
  const { currentSettings_state } = currentSettingsContext ?? {}
  const { aps_global, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});

  const { redirectToAppUserPage, appUserCollection, allowTokenCapture, allowPresenceTracking, allowDesktopToken, ignoreTokenAlert, allowCurrentUserTracking } = aps_appUserSettings ?? {}
  const { appSignIn, appLocationTracking, appSettings } = aps_global ?? {}
  const { forceDisclaimers, useCookiesDisclaimer, useSmsDisclaimer, usePushDisclaimer, useEmailDisclaimer } = appSignIn ?? {}
  const { allowLocationTracking, allowMovementTracking, movementDistance } = appLocationTracking ?? {}
  const { useProductionRedirect } = appSettings ?? {}

  const { sendFcm_topic } = useFcm()
  const { getProfileInfo, saveCurrentAppUserId, saveDailyUserCount, updatePresenceStatus } = useProfile()
  const { getAppUserFavs } = useFavorites()

  let _allowFcmPush = true // allowTokenCapture && (allowDesktopToken || isStandAlone)

  // local state
  // platform, accessRequests
  const initState = appUserReducerInitialState({ fn_cls, mainDb, mainAuth, platform, database_fns, currentAuthUser, aps_appUserSettings, pathViews, view })
  const [appUser_state, appUser_dispatch] = useReducer(appUserReducer, initState);

  const appUser_handlers = useMemo(
    () => appUserHandlers(appUser_dispatch),
    [appUser_dispatch]
  );

  const { appUserLocation, appUserChecked, appUser, appUsers, tokenAsked, accessClone } = appUser_state ?? {}
  const { profileData, loggedIn } = appUser ?? {}
  const appUserAccess = useAppUserAccess()
  const { appUserAccessType, appUserSessionKey, isAdminOrSuper, disclaimerResponses } = appUserAccess ?? {}

  _checkComponentTimes && start_fns.useTimeMeasurement('AppUserProvider', appUserChecked)

  const _forceDisclaimers = forceDisclaimers && !isAdminOrSuper && !disclaimerResponses && (useCookiesDisclaimer || useSmsDisclaimer || usePushDisclaimer || useEmailDisclaimer)

  const [_appUser_fns, setAppUserFns] = useState({})
  const [askForToken, setAskForToken] = useState()
  const [newLocation, setNewLocation] = useState()

  useEffect(() => {
    const loadFavorites = async () => {
      const favs = await getAppUserFavs()
      appUser_handlers.handleAmmendEventFavs(favs)
    }
    loadFavorites()
  }, [getAppUserFavs])

  // navigator.geolocation.watchPosition(function (position) {
  //   const { coords } = position
  //   const _location = {
  //     latitude: coords.latitude,
  //     longitude: coords.longitude
  //   }
  //   setNewLocation(_location)
  // });

  useEffect(() => {
    // start_handlers.handleAmmend_useEffect('setAppUserFns')j
    const _appUser_fns = appUserAccessPermissions({ appUsers, appUser, appUserCollection, eventAppUsers })
    setAppUserFns(_appUser_fns)
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [appUsers, appUserCollection, accessClone, eventAppUsers, appUserAccess])

  useEffect(() => {
    if (appUserAccessType < gEnums.accessLevels.appDataAdmin.value) {
      if (useProductionRedirect && hostName && hostName.indexOf('mobiledev') >= 0) {
        const _sharePathFull = sharePathFull.replace('mobiledev', 'mobile')
        window.location.href = _sharePathFull
      }
    }
    // eslint-disable-next-line
  }, [appUserAccessType, useProductionRedirect, hostName, pathViews.events]);

  useEffect(() => {
    if (allowCurrentUserTracking) {
      // check storage for _currentUserIdProp
      let storedUserId = currentHelpers.storageItem_get(_currentUserIdProp);
      if (!storedUserId) {
        // if it does not exist, save it to the database, and save it to 
        saveCurrentAppUserId({ pathViews })
        sendFcm_topic(storedUserId)
      }
      saveDailyUserCount({ pathViews })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [allowCurrentUserTracking])

  useEffect(() => {
    if (allowPresenceTracking) {
      updatePresenceStatus({ pathViews, currentAuthUser, presenceStatus })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [presenceStatus])

  // get the profile for the appUser
  useEffect(() => {
    getProfileInfo({
      currentAuthUser,
      pathViews,
      aps_appUserSettings,
      handleAmmendAppUser: appUser_handlers.handleSet_appUser,
      handleUpdateAppUserEventInfo: appUser_handlers.handleUpdateAppUserEventInfo,
      handleAppUserInfo: appUser_handlers.handleAppUserInfo
    })
    appUser_handlers.handleInit_currentAuthUser(currentAuthUser)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAuthUser])

  // get the profile for the appUser
  useEffect(() => {
    appUser_handlers.handleSet_eventAppUsers(eventAppUsers)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventAppUsers])

  useEffect(() => {
    if (appUserLocation && newLocation && allowLocationTracking && allowMovementTracking) {
      const _movementDistance = movementDistance * 100
      const allow = allowMovement(appUserLocation, newLocation, _movementDistance, true)
      if (allow) {
        appUser_handlers.handleAmmend_newLocation(newLocation)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newLocation])

  useEffect(() => {
    if (registration && loggedIn && !tokenAsked && _allowFcmPush) {
      setAskForToken(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration, loggedIn])

  useEffect(() => {
    if (askForToken) {
      setAskForToken()
      checkTokens(isSmartPhone, mainMessaging, registration, database_fns, profileData, appUserAccess, platform, pathViews, _allowFcmPush, ignoreTokenAlert).then(result => {
        appUser_handlers.handleAmmend_fcmTokens(result)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askForToken])

  useEffect(
    () => {
      const isLanding = view === 'events' || view === 'landing'
      if (redirectToAppUserPage && isLanding && appUserSessionKey && appUserCollection && !isAdminOrSuper) {
        if (appUserAccessType < gEnums.accessLevels.appDataAdmin.value) {
          const path = '/clients/' + pathViews.clients + '/events/' + pathViews.events + '/' + appUserCollection + '/' + appUserSessionKey
          navigate(path)
        }
      }
    },
    // eslint-disable-next-line
    [view, redirectToAppUserPage, appUserSessionKey, appUserCollection]
  )

  const provider = () => {
    if (loggedIn && _forceDisclaimers && _useDisclaimers) {
      return <Disclaimers appUser_handlers={appUser_handlers} />
    } else {
      return <AppUserContext.Provider value={{
        appUser_state,
        appUser_handlers,
        appUser_fns: _appUser_fns,
      }} >
        {props.children}
      </AppUserContext.Provider >
    }
  }

  return appUserChecked ? provider() : <div></div>
}

export default AppUserProvider

/**
 * 
 * @returns A boolean indicating whether the appUser has allowed notfications
 */
const requestPermissionGranted = async () => {
  if (Notification.permission === 'granted') {
    return true
  } else {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        return true
      } else {
        return false;
      }
    });
  }
}

const checkTokens = async (isSmartPhone, mainMessaging, registration, database_fns, profileData, appUserAccess, platform, pathViews, allowFcmPush, ignoreTokenAlert) => {

  // browserName = platform.name (chrome, firefox, safari, etc)
  // device_name = platform.device (desktop, android, iphone, pixel, etc)

  const { fcmType } = platform ?? {}

  const { fcmTokens } = appUserAccess ?? {}
  const _currentTokens = { ...fcmTokens }
  const _currentToken = fcmTokens ? fcmTokens[fcmType] : null
  const { tokenKey: currentTokenKey } = _currentToken ?? {}

  const appKeys = allAppKeys()
  const { publicVapidKey } = appKeys ?? {}

  const notificationRequestGranted = await requestPermissionGranted(publicVapidKey);

  if (notificationRequestGranted) {
    try {

      try {

        let allowMessaging = allowAppStorageAndFunctions()

        if (allowMessaging) {
          // await deleteToken(messaging)
          const newToken = await getToken(mainMessaging,
            {
              vapidKey: publicVapidKey,
              serviceWorkerRegistration: registration
            });

          let updateFcm = false; // Initialize updateFcm as false

          if (!_currentToken) {
            // Token does not exist, add new token
            _currentTokens[fcmType] = {
              tokenKey: newToken,
              timestamp: serverTimestamp(),
            };
            updateFcm = true;
          } else {
            // Token is different than current, update it
            if (currentTokenKey !== newToken) {
              console.log('⚙️ updated token', newToken)
              _currentTokens[fcmType] = {
                tokenKey: newToken,
                timestamp: serverTimestamp(),
              };
              updateFcm = true;
            }
          }

          if (updateFcm && allowFcmPush) {
            const cb = (res) => {
              return { fcmTokens: _currentTokens, notificationRequestGranted };
            };

            _showAppLogs.token && console.log('Adding token ', newToken);

            // Assuming updateAppUserFcmTokenToDatabase is a function that updates the database with _currentTokens
            updateAppUserFcmTokenToDatabase(isSmartPhone, database_fns, _currentTokens, profileData, pathViews, cb);
          } else {
            return { fcmTokens: _currentTokens, notificationRequestGranted };
          }
        }
      } catch (error) {
        console.error(error)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  return { fcmTokens: {}, notificationRequestGranted: null, messaging: null, token: null }
}